import { useEffect, useState } from "react";
import { fetchMentorRequests } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Inbox, Clock, CheckCircle2, XCircle, GraduationCap, Sparkles } from "lucide-react";
import MentorRequestRow from "./MentorRequestRow";

/**
 * Mentor Inbox — shown inside the Employee Dashboard when the current employee
 * has been requested as a mentor by someone else. Mirrors the HR queue but
 * scoped to mentor_id == employeeId.
 */
export default function MentorInboxPanel({ employeeId }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, completed: 0, total: 0 });

  const reload = async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      // Fetch all once to compute counts (small list, fine for prototype)
      const all = await fetchMentorRequests({ mentor_id: employeeId });
      const allItems = all.items || [];
      const c = { pending: 0, approved: 0, rejected: 0, completed: 0, total: allItems.length };
      for (const it of allItems) c[it.status] = (c[it.status] || 0) + 1;
      setCounts(c);
      setItems(filter === "all" ? allItems : allItems.filter((i) => i.status === filter));
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, filter]);

  const handleItemUpdate = (updated) => {
    // Re-pull all so counts stay accurate
    reload();
  };

  const empty = items.length === 0;

  return (
    <div className="space-y-5" data-testid="mentor-inbox-panel">
      {/* Hero / intro */}
      <div className="card-surface p-6 bg-gradient-to-br from-sky-50 via-white to-white border-sky-200">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-sky-500/15 border border-sky-300 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-sky-700" />
          </div>
          <div className="flex-1">
            <div className="label-eyebrow text-sky-700 mb-1">Mentor Gelen Kutusu</div>
            <h3 className="font-display text-2xl font-semibold tracking-tight">
              {counts.pending > 0 ? (
                <>
                  Seni mentor olarak isteyen <span className="text-sky-700">{counts.pending} talep</span> bekliyor
                </>
              ) : (
                "Bekleyen mentor talebin yok"
              )}
            </h3>
            <p className="text-sm text-slate-600 mt-1.5">
              Çalışma arkadaşların seni gelişim yolculuklarında mentor olarak görmüş.
              Bilgi aktarımı, hem onlara hem de senin liderlik gelişimine katkı sağlar.
            </p>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-4 gap-3">
          <Stat label="Bekleyen" value={counts.pending} accent="amber" Icon={Clock} />
          <Stat label="Kabul ettiklerin" value={counts.approved} accent="emerald" Icon={CheckCircle2} />
          <Stat label="Reddettiklerin" value={counts.rejected} accent="rose" Icon={XCircle} />
          <Stat label="Tamamlanan" value={counts.completed} accent="sky" Icon={GraduationCap} />
        </div>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="bg-white border border-slate-200 mb-4 p-1.5 h-auto flex-wrap">
          <TabsTrigger value="pending" data-testid="inbox-filter-pending" className="text-sm px-4 py-1.5 data-[state=active]:bg-amber-50 data-[state=active]:text-amber-900 data-[state=active]:shadow-sm">
            Bekleyen ({counts.pending})
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="inbox-filter-approved" className="text-sm px-4 py-1.5 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-900 data-[state=active]:shadow-sm">
            Kabul ettiklerin ({counts.approved})
          </TabsTrigger>
          <TabsTrigger value="rejected" data-testid="inbox-filter-rejected" className="text-sm px-4 py-1.5 data-[state=active]:bg-rose-50 data-[state=active]:text-rose-900 data-[state=active]:shadow-sm">
            Reddettiklerin ({counts.rejected})
          </TabsTrigger>
          <TabsTrigger value="completed" data-testid="inbox-filter-completed" className="text-sm px-4 py-1.5 data-[state=active]:bg-sky-50 data-[state=active]:text-sky-900 data-[state=active]:shadow-sm">
            Tamamlanan ({counts.completed})
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="inbox-filter-all" className="text-sm px-4 py-1.5 data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 data-[state=active]:shadow-sm">
            Tümü ({counts.total})
          </TabsTrigger>
        </TabsList>
        <TabsContent value={filter} className="space-y-3">
          {loading && (
            <div className="card-surface p-8" data-testid="mentor-inbox-loading">
              <div className="h-4 bg-slate-200/60 rounded animate-pulse w-1/3 mb-2" />
              <div className="h-3 bg-slate-100 rounded animate-pulse w-2/3" />
            </div>
          )}
          {!loading && empty && (
            <div className="card-surface p-12 text-center" data-testid="mentor-inbox-empty">
              <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              <h3 className="font-display text-lg font-semibold text-slate-900">
                Bu kategoride talep yok
              </h3>
              <p className="text-sm text-slate-500 mt-1.5 max-w-md mx-auto">
                Birisi seni mentor olarak istediğinde burada görünecek. Mentor önerilerinde çıkmak için
                profilindeki güçlü yönlerini güncel tutmanı öneririz.
              </p>
            </div>
          )}
          {!loading && items.map((it) => (
            <MentorRequestRow key={it.id} item={it} onUpdate={handleItemUpdate} perspective="mentor" currentUserId={employeeId} />
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, value, accent, Icon }) {
  const colors = {
    amber: "border-amber-300 text-amber-800 bg-amber-50",
    emerald: "border-emerald-300 text-emerald-800 bg-emerald-50",
    rose: "border-rose-300 text-rose-800 bg-rose-50",
    sky: "border-sky-300 text-sky-800 bg-sky-50",
  }[accent];
  return (
    <div className={`rounded-lg border ${colors} px-3 py-2.5`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[0.6rem] uppercase tracking-wider opacity-80">{label}</span>
        <Icon className="w-3.5 h-3.5 opacity-70" />
      </div>
      <div className="font-mono text-xl font-semibold">{value}</div>
    </div>
  );
}
